// Liquid-aware chipifier.
//
// Two chip kinds:
//   - "output" — {{ var }} or {{ var | filter: arg }}
//   - "tag"    — {% if x %}, {% endif %}, {% assign %}, {% case %}, etc.
//
// Round-trip: chipify -> dechipify reproduces the original Liquid source.
// `data-raw` holds the original source verbatim (base64'd) so any chip
// (including filtered output and tag blocks) restores byte-exact.

const LIQUID_RE = /\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}/g;

function encodeRaw(str) {
  // Base64 + UTF-8 safe, no DOM-unfriendly chars in attribute.
  return btoa(unescape(encodeURIComponent(str)));
}

function decodeRaw(b64) {
  try { return decodeURIComponent(escape(atob(b64))); } catch { return ""; }
}

function shortLabel(raw) {
  // For {{ x | filter }} → "x"; for {% if cond %} → "if cond" (truncated).
  const inner = raw.replace(/^\{\{|\}\}$|^\{%|%\}$/g, "").trim();
  if (raw.startsWith("{{")) {
    const head = inner.split("|")[0].trim();
    return head || inner;
  }
  // {% ... %}
  const trimmed = inner.length > 24 ? inner.slice(0, 22) + "…" : inner;
  return trimmed;
}

function makeChip(doc, raw) {
  const isTag = raw.startsWith("{%");
  const chip = doc.createElement("span");
  chip.className = isTag ? "liquid-chip liquid-chip-tag" : "liquid-chip liquid-chip-output merge-tag";
  chip.setAttribute("contenteditable", "false");
  chip.setAttribute("data-kind", isTag ? "tag" : "output");
  chip.setAttribute("data-raw", encodeRaw(raw));
  // Preserve legacy data-tag for simple {{word}} so old code reading it still works.
  if (!isTag) {
    const m = raw.match(/^\{\{\s*(\w+)\s*\}\}$/);
    if (m) chip.setAttribute("data-tag", m[1]);
  }
  chip.textContent = isTag ? `{% ${shortLabel(raw)} %}` : `{{${shortLabel(raw)}}}`;
  return chip;
}

function walkAndReplace(node) {
  if (node.nodeType === 3) {
    const text = node.textContent;
    LIQUID_RE.lastIndex = 0;
    if (!LIQUID_RE.test(text)) return;
    LIQUID_RE.lastIndex = 0;
    const frag = node.ownerDocument.createDocumentFragment();
    let lastIdx = 0;
    let m;
    while ((m = LIQUID_RE.exec(text)) !== null) {
      if (m.index > lastIdx) frag.appendChild(node.ownerDocument.createTextNode(text.slice(lastIdx, m.index)));
      frag.appendChild(makeChip(node.ownerDocument, m[0]));
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) frag.appendChild(node.ownerDocument.createTextNode(text.slice(lastIdx)));
    node.parentNode.replaceChild(frag, node);
    return;
  }
  if (node.nodeType !== 1) return;
  const tag = node.tagName;
  if (tag === "CODE" || tag === "PRE") return;
  if (tag === "SPAN" && (node.classList.contains("liquid-chip") || node.classList.contains("merge-tag"))) return;
  const children = Array.from(node.childNodes);
  children.forEach((c) => walkAndReplace(c));
}

export function chipifyHtml(html) {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  walkAndReplace(temp);
  return temp.innerHTML;
}

export function dechipifyHtml(html) {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;

  // Restore Liquid chips (new format with data-raw)
  temp.querySelectorAll("span.liquid-chip").forEach((chip) => {
    const raw = decodeRaw(chip.getAttribute("data-raw") || "");
    const out = raw || chip.textContent || "";
    chip.parentNode.replaceChild(document.createTextNode(out), chip);
  });

  // Backwards-compat: any legacy chip without data-raw (older drafts in DB).
  temp.querySelectorAll("span.merge-tag").forEach((chip) => {
    const tagName = chip.getAttribute("data-tag");
    const out = tagName ? `{{${tagName}}}` : (chip.textContent || "");
    chip.parentNode.replaceChild(document.createTextNode(out), chip);
  });

  return temp.innerHTML;
}
